/**
 * The live preview at the top of How it looks (DEC-318), for a button grid: one of its pads at rest, lit and waiting.
 *
 * A COPY of the pad on the canvas, never a second drawing (DEC-321, after the study's preview drifted from the item it
 * showed: "the guitar fx buttons preview / font doesn't match up"). The pad is cloned from the canvas with its fitted
 * name, texture and icon, inside a bare copy of its grid (finish, colour), and scaled to fit three across the panel.
 * Re-copied whenever the item changes, a beat after the canvas has re-fitted its names.
 */

import { createEffect, onCleanup } from 'solid-js';
import type { ButtonGridItem } from '../layout/projectStore.ts';

const STATES = [['rest', 'At rest'], ['lit', 'Lit'], ['waiting', 'Waiting']] as const;
const MAX_W = 82;
const MAX_H = 74;

export function GridPreview(props: { item: ButtonGridItem }) {
  let box: HTMLDivElement | undefined;
  let timer = 0;
  // The preview fills a moment after a change; by then the panel may have moved to another item or closed.
  let disposed = false;
  onCleanup(() => { disposed = true; });

  function fill(): void {
    if (disposed || !box) return;
    const src = document.querySelector<HTMLElement>(`.editable-item[data-item-id="${CSS.escape(props.item.id)}"]`);
    const grid = src?.querySelector<HTMLElement>('.bg-grid');
    const cell = grid?.querySelector<HTMLElement>('.bg-cell');
    if (!src || !grid || !cell || cell.offsetWidth === 0) { box.replaceChildren(); return; }
    const w = cell.offsetWidth, h = cell.offsetHeight;
    const s = Math.min(1, MAX_W / w, MAX_H / h);
    const figs = STATES.map(([state, caption]) => {
      const fig = document.createElement('figure');
      fig.dataset.state = state;
      const frame = document.createElement('div');
      frame.className = 'insp-preview-frame';
      frame.style.width = `${Math.round(w * s)}px`;
      frame.style.height = `${Math.round(h * s)}px`;
      // The item's colour lives in custom properties on its wrapper (--hue, --hue-ink): carry those, nothing else.
      for (const name of Array.from(src.style)) if (name.startsWith('--')) frame.style.setProperty(name, src.style.getPropertyValue(name));
      const g = grid.cloneNode(false) as HTMLElement;
      g.removeAttribute('style');
      g.dataset.title = 'off';
      g.dataset.disabled = 'false';
      g.removeAttribute('data-editing-cells');
      g.classList.add('insp-preview-grid');
      g.style.cssText = `--bg-cols:1;--bg-rows:1;width:${w + 6}px;height:${h + 6}px;padding:3px;gap:0;transform:scale(${s});transform-origin:0 0;`;
      for (const name of Array.from(grid.style)) if (name.startsWith('--') && name !== '--bg-cols' && name !== '--bg-rows' && name !== '--bp' && name !== '--qp') g.style.setProperty(name, grid.style.getPropertyValue(name));
      const c = cell.cloneNode(true) as HTMLButtonElement;
      c.disabled = false;
      c.tabIndex = -1;
      c.removeAttribute('data-press');
      c.querySelectorAll('.bg-cell-q, .bg-cell-qn, .bg-cell-rip').forEach((e) => e.remove());
      c.dataset.active = state === 'lit' ? 'true' : 'false';
      c.dataset.pending = 'false';
      if (state === 'waiting') {
        c.dataset.waiting = 'true';
        g.style.setProperty('--qp', '0.6');
        const q = document.createElement('span'); q.className = 'bg-cell-q';
        const n = document.createElement('span'); n.className = 'bg-cell-qn'; n.textContent = '2';
        c.append(q, n);
      } else {
        c.removeAttribute('data-waiting');
      }
      g.appendChild(c);
      frame.appendChild(g);
      const cap = document.createElement('figcaption');
      cap.textContent = caption;
      fig.append(frame, cap);
      return fig;
    });
    box.replaceChildren(...figs);
  }

  createEffect(() => {
    void props.item;
    window.clearTimeout(timer);
    // After the canvas has re-rendered and re-fitted its names (fitPadName runs on the next frame).
    timer = window.setTimeout(() => requestAnimationFrame(fill), 60);
  });
  onCleanup(() => window.clearTimeout(timer));

  return <div class="insp-preview" ref={(el) => (box = el)} aria-hidden="true" />;
}

/**
 * The preview for every other kind (DEC-321): a scaled copy of the item as it stands on the canvas. A single pad
 * (`.midi-button`) shows at rest and lit; anything else shows once, as it is. The copy sits in an `.insp-copy` wrapper
 * carrying the item's colour (its custom properties and `data-hue`), which the colour-as-light rules accept beside
 * `.editable-item` (skin-components.css), so it is lit exactly as the canvas is without being counted as an item.
 */
const ITEM_W = 260;
const ITEM_H = 120;
export function ItemPreview(props: { item: { id: string } }) {
  let box: HTMLDivElement | undefined;
  let timer = 0;
  // The preview fills a moment after a change; by then the panel may have moved to another item or closed.
  let disposed = false;
  onCleanup(() => { disposed = true; });
  function fill(): void {
    if (disposed || !box) return;
    const src = document.querySelector<HTMLElement>(`.editable-item[data-item-id="${CSS.escape(props.item.id)}"]`);
    const root = src ? (Array.from(src.children) as HTMLElement[]).find((c) => c.offsetWidth > 0 && !c.classList.contains('tl-mark')) : undefined;
    if (!src || !root) { box.replaceChildren(); return; }
    const w = root.offsetWidth, h = root.offsetHeight;
    const pad = root.classList.contains('midi-button');
    const states: [string, string][] = pad ? [['rest', 'At rest'], ['lit', 'Lit']] : [['as', '']];
    const each = pad ? (ITEM_W - 12) / 2 : ITEM_W;
    const s = Math.min(1, each / w, ITEM_H / h);
    box.replaceChildren(...states.map(([state, caption]) => {
      const fig = document.createElement('figure');
      fig.dataset.state = state;
      const frame = document.createElement('div');
      frame.className = 'insp-preview-frame';
      frame.style.width = `${Math.round(w * s)}px`;
      frame.style.height = `${Math.round(h * s)}px`;
      const wrap = document.createElement('div');
      wrap.className = 'insp-copy';
      wrap.dataset.hue = src.dataset.hue ?? 'none';
      wrap.dataset.kind = src.dataset.kind ?? '';
      for (const name of Array.from(src.style)) if (name.startsWith('--')) wrap.style.setProperty(name, src.style.getPropertyValue(name));
      wrap.style.cssText += `;position:absolute;left:0;top:0;width:${w}px;height:${h}px;transform:scale(${s});transform-origin:0 0;`;
      const c = root.cloneNode(true) as HTMLElement;
      c.querySelectorAll('.bg-cell-rip, .pad-rc').forEach((e) => e.remove());
      c.removeAttribute('data-press');
      if (pad) { c.dataset.on = state === 'lit' ? 'true' : 'false'; c.dataset.pressed = 'false'; }
      if (c instanceof HTMLButtonElement) c.disabled = false;
      c.setAttribute('tabindex', '-1');
      wrap.appendChild(c);
      frame.appendChild(wrap);
      fig.appendChild(frame);
      if (caption) { const cap = document.createElement('figcaption'); cap.textContent = caption; fig.appendChild(cap); }
      return fig;
    }));
  }
  createEffect(() => {
    void props.item;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => requestAnimationFrame(fill), 60);
  });
  onCleanup(() => window.clearTimeout(timer));
  return <div class="insp-preview" ref={(el) => (box = el)} aria-hidden="true" />;
}
